<template>
  <div>
    <h5>{{ $t("caisse.accesALaCaisse") }}</h5>
    <a-spin :spinning="accessLoading || usersLoading">
      <div
        v-for="(user, idx) in accessList"
        :key="user.user || idx"
        class="access-row"
      >
        <a-select
          show-search
          :filterOption="filterUserOption"
          v-model="user.user"
          class="w-50"
          :placeholder="$t('caisse.selectionnerUtilisateur')"
          @change="(val) => onUserSelect(idx, val)"
          :status="
            validationErrors[idx] && validationErrors[idx].user ? 'error' : ''
          "
        >
          <a-select-option v-for="u in usersList" :key="u._id" :value="u._id">
            {{ u.userName }}
            <span v-if="u.fullName && u.fullName !== ' '">
              ({{ u.fullName }})
            </span>
          </a-select-option>
        </a-select>
        <a-select
          v-model="user.permissions"
          mode="multiple"
          class="w-50"
          @change="(val) => onAccessPermissionChange(idx, val)"
          :placeholder="$t('caisse.roles')"
          :status="
            validationErrors[idx] && validationErrors[idx].permissions
              ? 'error'
              : ''
          "
        >
          <a-select-option value="OWNER">{{
            $t("caisse.proprietaireRole")
          }}</a-select-option>
          <a-select-option value="DEPENSE">{{
            $t("caisse.roleDepense")
          }}</a-select-option>
          <a-select-option value="DEPENSE_ENCAISSEMENT">{{
            $t("caisse.roleDepenseEncaissement")
          }}</a-select-option>
        </a-select>
        <a-button
          type="danger"
          shape="circle"
          icon="delete"
          @click="removeAccessUser(idx)"
        />
      </div>
      <div v-if="hasValidationErrors" class="text-danger mb-3">
        <a-icon type="warning" /> {{ $t("caisse.veuillezRemplirChamps") }}
      </div>
      <a-button
        type="dashed"
        icon="plus"
        @click="addAccessUser"
        style="margin-top: 12px"
        >{{ $t("caisse.ajouterUtilisateur") }}</a-button
      >
      <div style="margin-top: 16px">
        <a-button
          type="primary"
          :loading="accessSaving"
          @click="saveAccessList"
          >{{ $t("caisse.enregistrerAcces") }}</a-button
        >
      </div>
    </a-spin>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
export default {
  props: ["caisseId", "authorizedUsers"],
  data() {
    return {
      accessList: JSON.parse(JSON.stringify(this.authorizedUsers || [])),
      accessLoading: false,
      accessSaving: false,
      usersList: [],
      usersLoading: false,
      validationErrors: {},
    };
  },
  computed: {
    hasValidationErrors() {
      return Object.keys(this.validationErrors).length > 0;
    },
  },
  watch: {
    authorizedUsers(newVal) {
      this.accessList = JSON.parse(JSON.stringify(newVal || []));
      this.validationErrors = {};
    },
  },
  created() {
    this.fetchUsersList();
  },
  methods: {
    async fetchUsersList() {
      this.usersLoading = true;
      try {
        const res = await apiClient.get("/autorisation/buildingUsers/active");
        this.usersList = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("caisse.erreurChargementUtilisateurs"));
      } finally {
        this.usersLoading = false;
      }
    },
    addAccessUser() {
      this.accessList.push({
        user: "",
        userName: "",
        permissions: [],
      });
      this.validationErrors = {};
    },
    removeAccessUser(idx) {
      this.accessList.splice(idx, 1);
      this.validationErrors = {};
    },
    onUserSelect(idx, userId) {
      const userObj = this.usersList.find((u) => u._id === userId);
      if (userObj) {
        this.accessList[idx].user = userObj._id;
        this.accessList[idx].userName = userObj.userName;
      }
      this.validateField(idx, "user");
    },
    filterUserOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    onAccessPermissionChange(userIdx, newPermissions) {
      // A caisse can have several owners, so granting OWNER leaves the others untouched.
      this.accessList[userIdx].permissions = newPermissions;
      this.validateField(userIdx, "permissions");
    },
    validateField(idx, field) {
      if (!this.validationErrors[idx]) {
        this.validationErrors[idx] = {};
      }

      const value = this.accessList[idx][field];
      if (!value || (Array.isArray(value) && value.length === 0)) {
        this.validationErrors[idx][field] = true;
      } else {
        delete this.validationErrors[idx][field];
        if (Object.keys(this.validationErrors[idx]).length === 0) {
          delete this.validationErrors[idx];
        }
      }
    },
    validateAll() {
      this.validationErrors = {};
      this.accessList.forEach((user, idx) => {
        if (!user.user) {
          this.validateField(idx, "user");
        }
        if (!user.permissions || user.permissions.length === 0) {
          this.validateField(idx, "permissions");
        }
      });
      return !this.hasValidationErrors;
    },
    async saveAccessList() {
      if (!this.validateAll()) {
        this.$message.error(this.$t("caisse.veuillezRemplirChamps"));
        return;
      }

      // Several owners are allowed, but a caisse must keep at least one.
      const ownerCount = this.accessList.filter((u) =>
        u.permissions.includes("OWNER")
      ).length;
      if (ownerCount < 1) {
        this.$message.error(this.$t("caisse.auMoinsUnProprietaire"));
        return;
      }

      const uniqueUsers = new Set(this.accessList.map((u) => u.user));
      if (uniqueUsers.size !== this.accessList.length) {
        this.$message.error(this.$t("caisse.utilisateurEnDouble"));
        return;
      }

      this.accessSaving = true;
      try {
        await apiClient.patch(`/compV3/caisse/access/${this.caisseId}`, {
          authorizedUsers: this.accessList,
        });
        this.$message.success(this.$t("caisse.accesMisAJour"));
        this.$emit("updated");
      } catch (e) {
        console.log(e);
        let errorMsg = this.$t("caisse.erreurMiseAJourAcces");
        if (e.response && e.response.data && e.response.data.errorCode) {
          errorMsg = this.$t(`errorCode.${e.response.data.errorCode}`);
        }
        this.$message.error(errorMsg);
      } finally {
        this.accessSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.access-row {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
}

.text-danger {
  color: #ff4d4f;
}
</style>
